import { useCallback, useEffect, useRef, useState } from 'react';
import { WORKSPACE_TITLE_CLASS } from '../components/common/PageHeader';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../services/api';
import { withReturnTo } from '../components/common/returnPath';
import { LoadingRegion } from '../components/common/icons';
import { afterDecision, emptyReason, mergeQueue, step, toPrefetch } from './reviewCursor';
import { useLiveReload } from '../components/common/useLiveReload';
import type { ReviewItem } from '../types';

/**
 * Read what is about to go live, and say yes or no.
 *
 * Built as a PAGER rather than a list with checkboxes. The work is reading, and
 * a list makes reading the thing you leave the screen to do — you click into a
 * page, read it, come back, find your place, click the next one. Here the page
 * IS the screen, the decision is one key, and the next one is already loaded.
 *
 * The body is rendered by `previewPageBody`, which runs the deployers' own
 * builder. That is the whole basis for asking someone to sign off: a review
 * screen that renders the body its own way is asking about a page that will not
 * exist.
 */
export default function ReviewQueue() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const siteFilter = Number(searchParams.get('site')) || null;

  const [items, setItems] = useState<ReviewItem[]>([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [anyRequired, setAnyRequired] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Rendered bodies by page id. Kept across decisions so stepping back to
  // something you skipped is instant.
  const [bodies, setBodies] = useState<Record<number, string>>({});
  const [bodyError, setBodyError] = useState<Record<number, string>>({});

  const [acting, setActing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const noteRef = useRef<HTMLTextAreaElement | null>(null);

  const current: ReviewItem | undefined = items[index];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getReviewQueue(siteFilter ?? undefined);
      setItems(res.items);
      setAnyRequired(res.any_site_requires_review);
      setIndex((i) => (i < res.items.length ? i : 0));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the review queue');
    } finally {
      setLoading(false);
    }
  }, [siteFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // PAGES BECOME READY, AND GET REVIEWED, IN THE BACKGROUND. A build or a
  // growth session finishing re-reads the queue quietly and merges it, so new
  // pages join the end and ones decided elsewhere leave, without moving the
  // page on screen.
  const placeRef = useRef({ items, index });
  placeRef.current = { items, index };
  const refresh = useCallback(async () => {
    try {
      const res = await api.getReviewQueue(siteFilter ?? undefined);
      const next = mergeQueue(placeRef.current.items, placeRef.current.index, res.items);
      setAnyRequired(res.any_site_requires_review);
      setItems(next.items);
      setIndex(next.index);
    } catch {
      /* a missed refresh is fine: the next event tries again */
    }
  }, [siteFilter]);
  useLiveReload(
    (e) =>
      (e?.event_type === 'page_build' && ['completed', 'failed', 'error', 'cancelled'].includes(e.status)) ||
      (e?.event_type === 'page_refine' && e.status === 'completed') ||
      (e?.event_type === 'growth_agent_llm' && e.status === 'session_complete'),
    () => void refresh(),
    3_000,
  );

  // THE PREFETCH IS THE FEATURE. Fetching the next body while the reviewer is
  // still reading this one is the difference between paging and waiting.
  useEffect(() => {
    let cancelled = false;
    for (const item of toPrefetch(items, index)) {
      if (item.version_id == null) continue;
      if (bodies[item.page_id] || bodyError[item.page_id]) continue;
      api
        .previewPageBody(item.page_id, item.version_id)
        .then((res) => {
          if (!cancelled) {
            setBodies((prev) => ({ ...prev, [item.page_id]: res.html }));
          }
        })
        .catch((e) => {
          if (!cancelled) {
            setBodyError((prev) => ({
              ...prev,
              [item.page_id]: e instanceof Error ? e.message : 'Could not render',
            }));
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [items, index, bodies, bodyError]);

  const decide = useCallback(
    async (approve: boolean) => {
      if (!current || acting) return;
      setActing(true);
      setError(null);
      try {
        if (approve) {
          await api.approvePage(current.page_id, current.version_id);
        } else {
          await api.rejectPage(current.page_id, note);
        }
        const next = afterDecision(items, current.page_id, index);
        setItems(next.items);
        setIndex(next.index);
        setRejecting(false);
        setNote('');
      } catch (e) {
        const message = e instanceof Error ? e.message : 'That did not go through';
        setError(message);
        // A 409 means the page was rebuilt while it was being read, so what is
        // on screen is not what would publish. Reload rather than leave a stale
        // body under a button that just refused to act on it.
        if (/rebuilt/i.test(message)) load();
      } finally {
        setActing(false);
      }
    },
    [current, acting, items, index, note, load],
  );

  // Keyboard first: this screen is worked through, not clicked through.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      // Never steal keys from the rejection note. `j` is a letter before it is
      // a shortcut.
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) {
        if (e.key === 'Escape') {
          setRejecting(false);
          setNote('');
        }
        return;
      }
      if (e.key === 'j' || e.key === 'ArrowDown') {
        setIndex((i) => step(i, 1, items.length));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        setIndex((i) => step(i, -1, items.length));
      } else if (e.key === 'a') {
        decide(true);
      } else if (e.key === 'r') {
        setRejecting(true);
        // The note is the point of rejecting, so put the cursor in it.
        setTimeout(() => noteRef.current?.focus(), 0);
      } else if (e.key === 'Escape') {
        setRejecting(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items.length, decide]);

  if (loading) return <LoadingRegion label="Loading the review queue…" />;

  if (!items.length) {
    const reason = emptyReason({ anySiteRequiresReview: anyRequired, siteFilter });
    return (
      <div className="max-w-xl mx-auto py-16 text-center">
        <h1 className={WORKSPACE_TITLE_CLASS}>
          {reason === 'review-not-enabled' ? 'Review is off' : 'Nothing to review'}
        </h1>
        <p className="mt-2 text-gray-500 dark:text-gray-400">
          {reason === 'review-not-enabled' ? (
            <>
              No site asks for review yet, so pages marked <em>Ready to deploy</em>{' '}
              publish as soon as you deploy. Turn it on for a site under{' '}
              <strong>Target</strong> to have someone read pages first.
            </>
          ) : reason === 'site-caught-up' ? (
            'Nothing waiting for this site.'
          ) : (
            'Everything ready to publish has been read.'
          )}
        </p>
        <button
          onClick={() => navigate('/deliver')}
          className="mt-4 text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          Back to Deliver
        </button>
      </div>
    );
  }

  const body = current ? bodies[current.page_id] : undefined;
  const failed = current ? bodyError[current.page_id] : undefined;

  return (
    <div className="h-full flex flex-col">
      <header className="flex items-center gap-3 flex-wrap px-1 pb-3 border-b border-gray-200 dark:border-gray-700">
        <h1 className={WORKSPACE_TITLE_CLASS}>Review</h1>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {index + 1} of {items.length}
        </span>
        {/* The shortcuts, on screen. A keyboard-first tool that does not say so
            is a mouse tool with hidden features. */}
        <span className="hidden sm:inline text-xs text-gray-400 dark:text-gray-500">
          <kbd>j</kbd>/<kbd>k</kbd> move · <kbd>a</kbd> approve · <kbd>r</kbd> send back
        </span>
        <button
          onClick={() => navigate('/deliver')}
          className="ml-auto text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          Done
        </button>
      </header>

      {error && (
        <div className="mt-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-4 pt-3">
        {/* The queue, so the reviewer can see how much is left and jump about.
            Narrow on purpose — it is a position indicator, not the workspace. */}
        <aside className="hidden lg:block min-h-0 overflow-y-auto border-r border-gray-200 dark:border-gray-700 pr-2">
          {items.map((item, i) => (
            <button
              key={item.page_id}
              onClick={() => setIndex(i)}
              className={`w-full text-left px-2 py-2 rounded text-sm ${
                i === index
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-gray-100'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <span className="block truncate">{item.title}</span>
              <span className="block truncate text-xs text-gray-400 dark:text-gray-500">
                {item.site_name}
                {item.previously_approved && ' · rebuilt since approved'}
              </span>
            </button>
          ))}
        </aside>

        <main className="min-h-0 flex flex-col">
          {current && (
            <>
              <div className="pb-3">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {current.title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {current.site_name}
                  {current.path && ` · ${current.path}`}
                  {current.plan_name && ` · ${current.plan_name}`}
                </p>
                {/* A second look is a different job from a first one, and the
                    reviewer should know which they are doing before they read. */}
                {current.previously_approved && (
                  <p className="mt-2 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5">
                    You approved this before and it has been rebuilt since. The
                    approval was withdrawn automatically - this is the new text.
                  </p>
                )}
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 p-6">
                {failed ? (
                  <p className="text-sm text-red-600 dark:text-red-400">{failed}</p>
                ) : body === undefined ? (
                  <LoadingRegion label="Rendering…" />
                ) : (
                  // The same HTML the deployer's builder produces.
                  <article
                    className="prose dark:prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: body }}
                  />
                )}
              </div>

              <div className="pt-3 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => decide(true)}
                  disabled={acting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
                >
                  {acting ? 'Working…' : 'Approve'}
                </button>
                {!rejecting && (
                  <button
                    onClick={() => {
                      setRejecting(true);
                      setTimeout(() => noteRef.current?.focus(), 0);
                    }}
                    disabled={acting}
                    className="px-4 py-2 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 text-sm"
                  >
                    Send back
                  </button>
                )}
                <button
                  onClick={() => setIndex((i) => step(i, 1, items.length))}
                  className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  Skip →
                </button>
                <button
                  onClick={() =>
                    navigate(
                      withReturnTo(
                        `/page-builder/${current.page_id}`,
                        `${location.pathname}${location.search}`,
                        'the review queue',
                      ),
                    )
                  }
                  className="ml-auto text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Open in the page builder ↗
                </button>
              </div>

              {rejecting && (
                <div className="mt-2 flex items-start gap-2">
                  {/* The reason is the point. "Rejected" with no note makes the
                      next build a guess, so this is a step rather than a
                      confirm dialog. */}
                  <textarea
                    ref={noteRef}
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="What needs to change? The next person to pick this up will see it."
                    className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 dark:bg-gray-800"
                  />
                  <button
                    onClick={() => decide(false)}
                    disabled={acting}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm"
                  >
                    Send back
                  </button>
                  <button
                    onClick={() => {
                      setRejecting(false);
                      setNote('');
                    }}
                    className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
